import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from "vue";
import { FormInput, Checkbox, Submit } from "@/ts/components/form";
import { CalendarEvent } from "@/ts/stores/event";
import { useForm } from "vee-validate";
import * as Yup from "yup";
import { useErrors } from "@/ts/utils/api-functions";
import { trans, wTrans } from "laravel-vue-i18n";
import { useReCaptcha } from "vue-recaptcha-v3";
import { serialize } from "object-to-formdata";
import axios from "axios";
import { dateString } from "@/ts/utils/event-utils";

interface Props {
    event: CalendarEvent;
}


export default /*#__PURE__*/_defineComponent({
  props: {
    event: { type: null, required: true }
  },
  emits: ["submit", "back", "mounted"],
  setup(__props: any, { expose, emit }) {
  expose();

const props = __props as {
    event: CalendarEvent;
}



const apiErrors = useErrors();
const reCaptcha = useReCaptcha();
const success = ref(false);
const loading = ref(false);
const extraPersons = ref<{ full_name?: string; age?: number }[]>([
    {
        full_name: undefined,
        age: undefined,
    },
]);



const schema = computed(() =>
    Yup.object({
        event_name: Yup.string().required(),
        event_date: Yup.string().required(),
        confirmation_email: Yup.string().email().required(),
        full_name: Yup.string().required().label(wTrans("event.registration_form.full_name").value),
        age: Yup.number().required().label(wTrans("event.registration_form.age").value),
        email: Yup.string().email().required().label(wTrans("event.registration_form.email").value),
        privacy: Yup.bool().oneOf([true]).label(wTrans("event.registration_form.privacy_validation").value),
        extra_persons: Yup.array()
            .of(
                Yup.object({
                    full_name: Yup.string().label(wTrans("event.registration_form.full_name").value),
                    age: Yup.number().min(0).label(wTrans("event.registration_form.age").value),
                }),
            )
            .test({
                name: "Incomplete extra persons data",
                message: "Gelieve de ontbrekende gegevens in te vullen.",
                test: (extra_person) => {
                    if (extra_person) {
                        return extra_person.every(({ full_name, age }, index) => {
                            if (!age && (!full_name || full_name === "") && index !== extraPersons.value.length - 1) {
                                return false;
                            }
                            if (age && (!full_name || full_name === "")) {
                                return false;
                            }
                            if (full_name && full_name !== "" && !age) {
                                return false;
                            }
                            return true;
                        });
                    }
                    return true;
                },
            }),
    }),
);

const { handleSubmit, errors, values, submitCount, isSubmitting } = useForm({
    initialValues: {
        event_name: props.event.title.rendered,
        event_date: dateString(props.event),
        confirmation_email: props.event.acf.info.confirmation_email,
        full_name: undefined,
        age: undefined,
        email: undefined,
        privacy: false,
        extra_persons: [],
    },
    validationSchema: schema,
});

const onSubmit = handleSubmit(async (values, { resetForm }) => {
    loading.value = true;
    try {
        await reCaptcha!.recaptchaLoaded();
        const token = await reCaptcha!.executeRecaptcha("login");
        const formData = serialize(values, { indices: true });
        formData.append("g-recaptcha-response", token);
        formData.append("_wpcf7_unit_tag", "9226af4");
        await axios.post("/api/event-registration", formData);
        success.value = true;
        emit("submit", values);
    } catch (error) {
        apiErrors.parse(error);
    } finally {
        loading.value = false;
    }
});

function modifyExtraArray(index: number) {
    const editedEntry = extraPersons.value[index];
    editedEntry.full_name = values.extra_persons![index].full_name;
    editedEntry.age = values.extra_persons![index].age;

    const emptySlotAvailable =
        extraPersons.value.length > 1 &&
        extraPersons.value.find(({ full_name, age }) => {
            if ((!full_name || full_name !== "") && !age) {
                return true;
            }
            return false;
        });

    if ((!editedEntry.full_name || editedEntry.full_name === "") && !editedEntry.age) {
        extraPersons.value.splice(index, 1);
    } else if (!emptySlotAvailable && editedEntry && editedEntry.full_name && editedEntry.full_name !== "" && editedEntry.age) {
        extraPersons.value.push({
            full_name: undefined,
            age: undefined,
        });
    }
}

const __returned__ = { props, apiErrors, reCaptcha, success, loading, extraPersons, emit, schema, handleSubmit, errors, values, submitCount, isSubmitting, onSubmit, modifyExtraArray, FormInput, Checkbox, Submit, trans }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})